/**
 * @file Firebase
 * @module store/firebase
 * @desc Configures Firebase and oversees its usage
 * @since v4.12.0-prod...
 * @backport v5.9.3-beta.20240827.e9e4b63
 */

/**
 * @typedef {import('firebase/app-check').AppCheck} AppCheck
 */

import { initializeApp } from 'firebase/app'
import { getAnalytics, isSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics'
import { getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  onAuthStateChanged,
  onIdTokenChanged,
  reauthenticateWithCredential,
  SAMLAuthProvider,
  sendEmailVerification,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updatePassword,
} from 'firebase/auth'

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  initializeFirestore,
  limit,
  onSnapshot,
  orderBy,
  persistentLocalCache,
  persistentMultipleTabManager,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore'

const logger = console

const {
  NODE_ENV,
  VUE_APP_FIREBASE_STORAGE: FIREBASE_STORAGE,
  VUE_APP_FIREBASE_API_KEY: FIREBASE_API_KEY,
  VUE_APP_FIREBASE_APP_ID: FIREBASE_APP_ID,
  VUE_APP_FIREBASE_DATABASE_ID: FIREBASE_DATABASE_ID,
  VUE_APP_FIREBASE_DATABASE_URL: FIREBASE_DATABASE_URL,
  VUE_APP_FIREBASE_SEARCH_DATABASE_ID: SEARCH_DATABASE_ID,
  VUE_APP_FIREBASE_MEASUREMENT_ID: FIREBASE_MEASUREMENT_ID,
  VUE_APP_FIREBASE_MESSAGING_SENDER_ID: FIREBASE_MESSAGING_SENDER_ID,
  VUE_APP_FIREBASE_SCOPE: FIREBASE_SCOPE,
  VUE_APP_GOOGLE_CLIENT_ID: GOOGLE_CLIENT_ID,
  VUE_APP_GOOGLE_PROJECT: GOOGLE_PROJECT,

  // Firebase App Check
  VUE_APP_FIREBASE_APP_CHECK_DEBUG_TOKEN: FIREBASE_APP_CHECK_DEBUG_TOKEN,
  VUE_APP_RECAPTCHA_ENTERPRISE_SITE_KEY: RECAPTCHA_ENTERPRISE_SITE_KEY,

  // Firebase Auth
  VUE_APP_FIREBASE_AUTH_DOMAIN: FIREBASE_AUTH_DOMAIN,

  // Domain & Tenant IDs
  VUE_APP_DOMAIN_NAME,
  VUE_APP_TENANT_ID,
  VUE_APP_DOMAIN_NAME_FOR_ADOPS_TENANT: DOMAIN_NAME_FOR_ADOPS_TENANT,
  VUE_APP_TENANT_ID_FOR_ADOPS_TENANT: TENANT_ID_FOR_ADOPS_TENANT,
  VUE_APP_DOMAIN_NAME_FOR_DEFAULT_TENANT: DOMAIN_NAME_FOR_DEFAULT_TENANT,
  VUE_APP_TENANT_ID_FOR_DEFAULT_TENANT: TENANT_ID_FOR_DEFAULT_TENANT,
} = process.env

export const FIREBASE_DOMAIN_NAME = VUE_APP_DOMAIN_NAME
export const FIREBASE_TENANT_ID = VUE_APP_TENANT_ID

export const FIREBASE_DOMAIN_NAME_FOR_DEFAULT_TENANT = DOMAIN_NAME_FOR_DEFAULT_TENANT
export const FIREBASE_TENANT_ID_FOR_DEFAULT_TENANT = TENANT_ID_FOR_DEFAULT_TENANT

export const FIREBASE_DOMAIN_NAME_FOR_ADOPS_TENANT = DOMAIN_NAME_FOR_ADOPS_TENANT
export const FIREBASE_TENANT_ID_FOR_ADOPS_TENANT = TENANT_ID_FOR_ADOPS_TENANT

const IS_ENV_DEV = /dev/.test(NODE_ENV)
const IS_ENV_UAT = /uat/.test(GOOGLE_PROJECT)

const FIREBASE_CONFIG = {
  apiKey: FIREBASE_API_KEY,
  appId: FIREBASE_APP_ID,
  authDomain: FIREBASE_AUTH_DOMAIN,
  clientId: GOOGLE_CLIENT_ID,
  databaseURL: FIREBASE_DATABASE_URL,
  projectId: GOOGLE_PROJECT,
  measurementId: FIREBASE_MEASUREMENT_ID,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  scopes: FIREBASE_SCOPE,
  storageBucket: FIREBASE_STORAGE,
}

// Initialize Firebase & export it.
const firebaseApp = initializeApp(FIREBASE_CONFIG)

/**
 * @function getFireAppCheck
 * @desc Create a Firebase App Check instance with the given Firebase
 * app instance. Gracefully handles initialization errors when App Check
 * is not yet fully configured in Firebase Console.
 * @param {FirebaseApp} app The Firebase app instance.
 * @return {AppCheck} The Firebase App Check instance.
 * @see https://firebase.google.com/docs/app-check/web/recaptcha-provider
 */
export const getFireAppCheck = (app) => {
  try {
    if (IS_ENV_DEV) {
      logger.log('Environment detected: development.')

      if (!FIREBASE_APP_CHECK_DEBUG_TOKEN) {
        logger.warn('FIREBASE_APP_CHECK_DEBUG_TOKEN is not set. App Check may not function correctly in development.')
      } else {
        // Configure Firebase App Check with a debug token for local development.
        // Set to string to support multi-browser and multi-device dev sessions.
        // eslint-disable-next-line no-restricted-globals
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = FIREBASE_APP_CHECK_DEBUG_TOKEN
      }
    } else {
      logger.log('Environment detected: production.')
    }

    return initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
      isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
    })
  } catch (error) {
    logger.error('App Check initialization failed:', error.message)
    logger.warn('App Check is not yet fully configured in Firebase Console. Auth flow will proceed without App Check tokens.')
    // Return a mock/placeholder to prevent downstream errors
    return null
  }
}

/**
 * @const {AppCheck} FireAppCheck
 * @desc This is the Firebase App Check instance initialized
 * with our Firebase app instance. Uses `let` because `recoverAppCheck()`
 * reassigns this binding after clearing throttle state — ES module live
 * bindings ensure importers always see the latest value.
 */
// eslint-disable-next-line import/no-mutable-exports
export let FireAppCheck = getFireAppCheck(firebaseApp)

/**
 * Clear App Check throttle state from IndexedDB.
 * When reCAPTCHA Enterprise returns a transient 403, the Firebase SDK
 * enters a 24-hour throttle that blocks all subsequent token requests.
 * This function removes that cached state so the next getToken attempt
 * will contact the reCAPTCHA server fresh.
 * @returns {Promise<boolean>} true if state was cleared successfully
 */
export const clearAppCheckThrottleState = () => new Promise((resolve) => {
  const DB_NAME = 'firebase-app-check-store'

  if (typeof indexedDB === 'undefined') {
    resolve(false)
    return
  }

  const request = indexedDB.deleteDatabase(DB_NAME)
  request.onsuccess = () => {
    logger.info('[App Check] Throttle state cleared from IndexedDB')
    resolve(true)
  }
  request.onerror = () => {
    logger.warn('[App Check] Failed to clear throttle state')
    resolve(false)
  }
  request.onblocked = () => {
    logger.warn('[App Check] IndexedDB delete blocked by open connections')
    resolve(false)
  }
})

/**
 * Attempt to recover App Check from a throttled state.
 * Clears local IndexedDB throttle cache and reinitializes App Check.
 * Call this when Firestore returns permission errors that may be caused
 * by a stale App Check throttle rather than genuine rule violations.
 * @returns {Promise<boolean>} true if recovery produced a valid token
 */
export const recoverAppCheck = async () => {
  logger.info('[App Check] Attempting throttle recovery...')
  const cleared = await clearAppCheckThrottleState()
  if (!cleared) return false

  // Reinitialize App Check after clearing throttle state
  FireAppCheck = getFireAppCheck(firebaseApp)
  if (!FireAppCheck) return false

  // Attempt a fresh token acquisition
  try {
    await getToken(FireAppCheck, /* forceRefresh */ true)
    logger.info('[App Check] Recovery successful - token acquired')
    return true
  } catch (err) {
    logger.warn('[App Check] Recovery failed - token still unavailable:', err.message)
    return false
  }
}

// Initialize Remote Config and get a reference to the service.
const FireRemoteConfig = getRemoteConfig(firebaseApp)

// Set minimum fetch interval to 1 hour.
// During development, it's recommended to set a relatively low minimum fetch interval.
FireRemoteConfig.settings.minimumFetchIntervalMillis = 3600

// @TODO Download a generated JSON file that includes all default RemoteConfig values and include it in our app bundle.
const FIRESTORE_COLLECTION = {
  AUTH: 'person',
  ORG: 'organization',
}

export const API_ENDPOINT = {
  SERVICES: IS_ENV_UAT ? 'client_services' : 'services',
}

// Set default config values. Remote Config will use these
// values if no value is set in the Remote Config console.
FireRemoteConfig.defaultConfig = {
  API_ENDPOINT_TIMESHEET_SERVICES: API_ENDPOINT.SERVICES,
  FIRESTORE_COLLECTION_AUTH: FIRESTORE_COLLECTION.AUTH,
  FIRESTORE_COLLECTION_ORG: FIRESTORE_COLLECTION.ORG,
}

// Fetch and activate Remote Config values.
fetchAndActivate(FireRemoteConfig).then((activated) => {
  if (activated) {
    FIRESTORE_COLLECTION.AUTH = getValue(FireRemoteConfig, 'FIRESTORE_COLLECTION_AUTH').asString()
    FIRESTORE_COLLECTION.ORG = getValue(FireRemoteConfig, 'FIRESTORE_COLLECTION_ORG').asString()

    API_ENDPOINT.SERVICES = getValue(FireRemoteConfig, 'API_ENDPOINT_TIMESHEET_SERVICES').asString()

    logger.log('Remote Config was activated')
  } else {
    logger.log('Remote Config was not activated')
  }
}).catch((error) => {
  logger.error('Remote Config Error', error)
})

// Save configured Store references.
let analytics = null

isSupported().then((isSupportedApp) => {
  if (isSupportedApp) analytics = getAnalytics(firebaseApp)
})

// Lazy-initialize Firestore to defer long-polling channel connections
// until first actual use, improving initial page load performance
let FirestoreInstance = null
let SearchFirestoreInstance = null

const getFirestoreInstance = () => {
  if (!FirestoreInstance) {
    const firestoreSettings = {
      localCache: persistentLocalCache(/* settings */{
        tabManager: persistentMultipleTabManager(),
      }),
    }

    // Support alternative Firestore databases (e.g., 'next' for testing)
    if (FIREBASE_DATABASE_ID) {
      logger.info(`[Firebase] Using Firestore database: ${FIREBASE_DATABASE_ID}`)
    }

    FirestoreInstance = initializeFirestore(firebaseApp, firestoreSettings, FIREBASE_DATABASE_ID)
  }
  return FirestoreInstance
}

// Separate Firestore instance for the search collection, which lives on
// a different database (e.g., 'next'). Falls back to main instance if
// no separate search database is configured.
const getSearchFirestoreInstance = () => {
  // Share the main instance when IDs match or no search DB is configured
  if (!SEARCH_DATABASE_ID || SEARCH_DATABASE_ID === FIREBASE_DATABASE_ID) {
    return getFirestoreInstance()
  }

  if (!SearchFirestoreInstance) {
    const firestoreSettings = {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    }

    logger.info(`[Firebase] Using Search Firestore database: ${SEARCH_DATABASE_ID}`)
    SearchFirestoreInstance = initializeFirestore(firebaseApp, firestoreSettings, SEARCH_DATABASE_ID)
  }
  return SearchFirestoreInstance
}

// Eagerly initialize Firestore so the first call doesn't pay the
// initializeFirestore cost. When database IDs match, search shares
// this instance via getSearchFirestoreInstance → getFirestoreInstance.
setTimeout(getFirestoreInstance, 0)

// getFirestore(firebaseApp)
export const Fireauth = getAuth(firebaseApp)

const canExposeE2ECustomAuth = () => {
  if (typeof window === 'undefined') return false

  const hostname = window.location.hostname || ''
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1'
  if (isLocalHost) return true

  const hostingChannel = process.env.VUE_APP_HOSTING_CHANNEL || ''
  const hostingSite = process.env.VUE_APP_HOSTING_SITE || ''
  const isPreviewChannelHost = !!hostingChannel
    && !!hostingSite
    && hostname.endsWith('.web.app')
    && hostname.includes(`${hostingSite}--${hostingChannel}`)

  return isPreviewChannelHost
}

// Expose custom-token sign-in for E2E testing (Playwright).
// Safe: requires a valid Admin SDK-signed token to use.
if (canExposeE2ECustomAuth()) {
  // eslint-disable-next-line no-underscore-dangle
  window.__e2eSignIn = async (token, tenantId) => {
    if (tenantId) Fireauth.tenantId = tenantId
    return signInWithCustomToken(Fireauth, token)
  }

  // Auto-consume credentials injected by Playwright's auth fixture.
  // The fixture sets window.__e2eCredentials via context.addInitScript
  // before any page scripts run, so this block fires on module load.
  // eslint-disable-next-line no-underscore-dangle
  if (window.__e2eCredentials) {
    // eslint-disable-next-line no-underscore-dangle
    const { token, tenantId } = window.__e2eCredentials
    // eslint-disable-next-line no-underscore-dangle
    delete window.__e2eCredentials
    // eslint-disable-next-line no-underscore-dangle
    window.__e2eSignIn(token, tenantId)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('E2E auto-sign-in failed:', err.code || err.message)
      })
  }
}

/**
 * Wait for Firebase Auth to have a current user.
 * Resolves with the user or null if timeout expires.
 * @param {number} [timeoutMs=10000] - Maximum time to wait
 * @returns {Promise<import('firebase/auth').User|null>}
 */
export const waitForAuth = (timeoutMs = 10000) => {
  if (Fireauth.currentUser) return Promise.resolve(Fireauth.currentUser)

  return new Promise((resolve) => {
    let unsubscribe = null

    const timer = setTimeout(() => {
      if (unsubscribe) unsubscribe()
      resolve(null)
    }, timeoutMs)

    unsubscribe = onAuthStateChanged(Fireauth, (user) => {
      clearTimeout(timer)
      if (unsubscribe) unsubscribe()
      resolve(user)
    })
  })
}

/**
 * Wait for App Check token availability.
 * Resolves true when token is fetched, false on timeout/error/unavailable.
 * On throttle detection, attempts one recovery cycle (clear + retry).
 * @param {number} [timeoutMs=5000] - Maximum time to wait
 * @returns {Promise<boolean>}
 */
export const waitForAppCheck = (timeoutMs = 5000) => new Promise((resolve) => {
  // If App Check is not initialized (null), resolve as not ready
  // This allows auth flow to proceed without blocking on App Check
  if (!FireAppCheck) {
    logger.info('App Check not initialized - auth will proceed without App Check tokens')
    resolve(false)
    return
  }

  const timer = setTimeout(() => resolve(false), timeoutMs)

  getToken(FireAppCheck, false)
    .then(() => {
      clearTimeout(timer)
      resolve(true)
    })
    .catch(async (err) => {
      clearTimeout(timer)
      const isThrottled = /throttle|too many attempts|429/i.test(err.message || '')

      if (isThrottled) {
        logger.warn('[App Check] Throttle detected during waitForAppCheck, attempting recovery...')
        const recovered = await recoverAppCheck()
        resolve(recovered)
      } else {
        logger.warn('[App Check] Token acquisition failed:', err.message)
        resolve(false)
      }
    })
})

/**
 * Wait for Firebase readiness steps used by auth bootstrap.
 * Helps reduce startup races between auth state and App Check tokening.
 * @param {Object} [options]
 * @param {number} [options.authTimeoutMs=10000]
 * @param {number} [options.appCheckTimeoutMs=5000]
 * @param {string} [options.expectedEmail]
 * @returns {Promise<{authUser: import('firebase/auth').User|null, appCheckReady: boolean, authMatchesExpected: boolean}>}
 */
export const waitForFirebaseReady = async ({
  authTimeoutMs = 10000,
  appCheckTimeoutMs = 5000,
  expectedEmail = null,
} = {}) => {
  const authUser = await waitForAuth(authTimeoutMs)
  const appCheckReady = await waitForAppCheck(appCheckTimeoutMs)

  const normalizedExpected = expectedEmail && expectedEmail.toLowerCase()
  const normalizedCurrent = authUser?.email && authUser.email.toLowerCase()
  const authMatchesExpected = !normalizedExpected || normalizedExpected === normalizedCurrent

  return { authUser, appCheckReady, authMatchesExpected }
}

export const setTenantIdOnFireauth = (tenantId) => {
  Fireauth.tenantId = tenantId
}

export const getTenantIdFromFireauth = () => Fireauth.tenantId

const firecollection = {
  change: () => collection(getFirestoreInstance(), 'change'),
  docs: () => collection(getFirestoreInstance(), 'app/docs/links'),
  domain: () => collection(getFirestoreInstance(), 'domain'),
  enterprise: () => collection(getFirestoreInstance(), 'enterprise'),
  message: () => collection(getFirestoreInstance(), 'message'),
  organization: () => collection(getFirestoreInstance(), 'organization'),
  person: () => collection(getFirestoreInstance(), 'person'),
  project: () => collection(getFirestoreInstance(), 'project'),
  'project-merge-backup': () => collection(getFirestoreInstance(), 'project-merge-backup'),
  'project-note': () => collection(getFirestoreInstance(), 'project-note'),
  'project-platform': () => collection(getFirestoreInstance(), 'project-platform'),
  'project-status': () => collection(getFirestoreInstance(), 'project-status'),
  'project-type': () => collection(getFirestoreInstance(), 'project-type'),
  provider: () => collection(getFirestoreInstance(), 'provider'),
  report: () => collection(getFirestoreInstance(), 'report'),
  search: () => collection(getSearchFirestoreInstance(), 'search'),
  setting: () => collection(getFirestoreInstance(), 'setting'),
  timeEntry: () => collection(getFirestoreInstance(), 'timeEntry'),
  error(catcher = logger.error, collectionName = 'operation') {
    return (FirebaseError) => {
      const { code, message: firebaseMessage } = FirebaseError

      const hasMissingOrInsufficientPermissions = /missing or insufficient permissions/i.test(firebaseMessage)

      // Security: Don't expose collection names or detailed permissions info to unauthenticated users
      // Instead, log full details to Sentry and show generic message to user
      const userMessage = hasMissingOrInsufficientPermissions
        ? 'Access Denied - Please sign in to continue'
        : firebaseMessage || 'Record Not Found'

      // Log detailed error info for debugging (will go to Sentry)
      const detailedMessage = hasMissingOrInsufficientPermissions
        ? `${firebaseMessage.replace('.', '')} for ${collectionName}.`
        : firebaseMessage

      const error = {
        code: code || 404,
        layer: 'app/store',
        message: userMessage,
        // Include detailed info for Sentry/logging but not shown to user
        _debugInfo: {
          collectionName,
          originalMessage: detailedMessage,
          firebaseCode: code,
        },
      }

      // Log to console in development for debugging
      if (IS_ENV_DEV) {
        logger.error('[Firestore Error]', {
          collection: collectionName,
          code,
          message: detailedMessage,
        })
      }

      return catcher({ error })
    }
  },
  /**
   * Watches documents, running observers in response to document changes.
   * @param {string} collectionName The name of the document's collection.
   * @param {string} docId The id of the document to watch, e.g. the email for a person record.
   * @param {Object<{ error, next }>} observer The functions to call when the document changes.
   * @returns {Promise<() => void>} Resolves to a function used to stop watching the document.
   */
  async watch(collectionName, docId, observer) {
    const currentDoc = doc(getFirestoreInstance(), collectionName, docId)
    return onSnapshot(currentDoc, observer)
  },
  async collection(collectionName, catcher) {
    const currentCollection = this[collectionName]()
    const docs = await getDocs(currentCollection).catch(this.error(catcher, collectionName))
    const results = []

    // Store responses are not real arrays; Instead, they offer a custom forEach method.
    if (docs && docs.forEach) {
      docs.forEach((document) => {
        const result = document.data()

        if (result) result.docId = document.id

        results.push(result)
      })
    }

    return results
  },
  async get(collectionName, docId, catcher) {
    const currentDoc = doc(getFirestoreInstance(), collectionName, docId)

    const document = await getDoc(currentDoc).catch(this.error(catcher, collectionName))

    // Preserve normalized Firestore errors so auth flows can distinguish
    // permission failures from true missing documents.
    if (document && document.error) {
      return document
    }

    const response = (document && !document.error && document.exists() && document.data()) || null

    if (response) {
      response.docId = document.id
    }

    return response
  },
  async set(collectionName, docId, newData, options) {
    const currentCollection = this[collectionName]()

    const output = () => firecollection.get(collectionName, docId)

    return setDoc(doc(currentCollection, docId), newData, options).then(output)
  },
  async delete(collectionName, docId) {
    const currentCollection = this[collectionName]()
    return deleteDoc(doc(currentCollection, docId))
  },

  orderBy(field, order = 'asc') {
    return orderBy(field, order)
  },
  where(whereClauses) {
    return whereClauses.map((clause) => {
      const [field, operator, value] = clause
      return where(field, operator, value)
    })
  },
  async query(collectionName, whereClauses) {
    const currentCollection = this[collectionName]()
    const querySnapshot = query(currentCollection, ...whereClauses)
    const docs = await getDocs(querySnapshot).catch(this.error(logger.error, collectionName))
    const results = []

    // Store responses are not real arrays; Instead, they offer a custom forEach method.
    if (docs && docs.forEach) {
      docs.forEach((document) => {
        const result = document.data()

        if (result) result.docId = document.id

        results.push(result)
      })
    }

    return results
  },
  async findAll(collectionName, whereClauses, limitCount = 100, sortOrder, catcher) {
    const currentCollection = this[collectionName]()
    const querySnapshot = query(currentCollection, ...whereClauses, limit(limitCount), sortOrder)
    const docs = await getDocs(querySnapshot).catch(this.error(catcher, collectionName))
    const results = []

    // Store responses are not real arrays; Instead, they offer a custom forEach method.
    if (docs && docs.forEach) {
      docs.forEach((document) => {
        const result = document.data()

        if (result) result.docId = document.id

        results.push(result)
      })
    }

    return results
  },

  auth(docId, catcher) {
    if (docId) {
      return this.get(FIRESTORE_COLLECTION.AUTH, docId, catcher)
    }

    return this.collection(FIRESTORE_COLLECTION.AUTH, catcher)
  },

  org(docId, catcher) {
    if (docId) {
      return this.get(FIRESTORE_COLLECTION.ORG, docId, catcher)
    }

    return this.collection(FIRESTORE_COLLECTION.ORG, catcher)
  },

  /**
   * Fetches search options from the search collection.
   * Results are pre-filtered by Firestore security rules based on user access.
   *
   * - Internal users: See all search options where access.internal === true
   * - External users: See only options where their billing codes are in access.externalBillingCodes
   *
   * @param {Object} options - Query options
   * @param {string[]} [options.billingCodes] - Calling external user's organization billing
   *   codes, used to scope the access.externalBillingCodes match. Ignored for internal users.
   * @param {string} [options.reportKey] - Filter by specific reportKey (e.g., 'pacing', 'people')
   * @param {string} [options.category] - Filter by category (e.g., 'network', 'person')
   * @param {function} [catcher] - Error handler function
   * @returns {Promise<Array>} Array of search option documents
   *
   * @example
   * // Get all search options for the user
   * const allOptions = await firecollection.searchOptions()
   *
   * // Get only pacing-related options
   * const pacingOptions = await firecollection.searchOptions({ reportKey: 'pacing' })
   *
   * // Get only network category options
   * const networkOptions = await firecollection.searchOptions({ category: 'network' })
   */
  async searchOptions(options = {}, catcher) {
    // Fail fast if no authenticated user. The SearchBar component guards
    // on haveCurrentUser before calling, so this is a safety net only.
    if (!Fireauth.currentUser) {
      const error = { code: 'unauthenticated', message: 'No authenticated user available for search query' }
      logger.warn('[Firestore] searchOptions called before auth ready')
      if (catcher) return catcher({ error })
      return []
    }

    const { billingCodes = [], reportKey, category } = options
    const searchCollection = this.search()

    // Build query with access constraint + optional filters.
    // Firestore security rules check resource.data fields (access.internal,
    // access.externalBillingCodes) so client queries MUST include matching
    // where() clauses — "rules are not filters".
    const queryConstraints = []

    const { email } = Fireauth.currentUser
    const isInternal = email.endsWith('@adops.com') || email.endsWith('@brightdefense.com')

    if (isInternal) {
      queryConstraints.push(where('access.internal', '==', true))
    } else if (billingCodes.length > 0) {
      queryConstraints.push(
        where('access.externalBillingCodes', 'array-contains-any', billingCodes.slice(0, 10)),
      )
    }

    queryConstraints.push(orderBy('sortOrder', 'asc'))

    let querySnapshot

    // Apply filters if provided
    if (reportKey) {
      const filteredQuery = query(searchCollection, where('reportKey', '==', reportKey), ...queryConstraints)
      querySnapshot = await getDocs(filteredQuery).catch(this.error(catcher, 'search'))
    } else if (category) {
      const filteredQuery = query(searchCollection, where('category', '==', category), ...queryConstraints)
      querySnapshot = await getDocs(filteredQuery).catch(this.error(catcher, 'search'))
    } else {
      const allQuery = query(searchCollection, ...queryConstraints)
      querySnapshot = await getDocs(allQuery).catch(this.error(catcher, 'search'))
    }

    const results = []

    // Process query results
    if (querySnapshot && querySnapshot.forEach) {
      querySnapshot.forEach((document) => {
        const result = document.data()

        if (result) {
          result.docId = document.id
          results.push(result)
        }
      })
    }

    return results
  },

  /**
   * Creates or updates a search document.
   * Only accessible by internal admin users (enforced by security rules).
   *
   * @param {string} docId - Document ID (format: {reportKey}_{identifier})
   * @param {Object} searchData - Search document data
   * @param {function} [catcher] - Error handler function
   * @returns {Promise<Object>} Updated search document
   */
  async upsertSearchOption(docId, searchData, catcher) {
    const searchCollection = this.search()

    // Add timestamps
    const now = new Date()
    const dataWithTimestamps = {
      ...searchData,
      updatedAt: now,
      lastSyncedAt: now,
    }

    // Set createdAt only if it doesn't exist
    if (!searchData.createdAt) {
      dataWithTimestamps.createdAt = now
    }

    return setDoc(doc(searchCollection, docId), dataWithTimestamps, { merge: true })
      .then(async () => {
        const document = await getDoc(doc(getSearchFirestoreInstance(), 'search', docId))
        const response = (document && document.exists() && document.data()) || null
        if (response) response.docId = document.id
        return response
      })
      .catch(this.error(catcher, 'search'))
  },

  /**
   * Deletes a search document.
   * Only accessible by dev users (enforced by security rules).
   *
   * @param {string} docId - Document ID to delete
   * @returns {Promise<void>}
   */
  async deleteSearchOption(docId) {
    const searchCollection = this.search()
    return deleteDoc(doc(searchCollection, docId))
  },
}

const fireanalytics = {
  event(eventName, eventData) {
    if (analytics) {
      logEvent(analytics, eventName, eventData)
    }
  },
  async setUser(analyticsData) {
    const { analyticsUser, personId } = analyticsData

    setUserId(analytics, personId)

    return setUserProperties(analytics, analyticsUser)
  },
}

const providerMap = {
  google({ isInternalDomain = false }) {
    const identityProvider = new GoogleAuthProvider()

    // Base scopes for all users
    identityProvider.addScope('email')
    identityProvider.addScope('profile')

    // Add Google Sheets API scopes for @adops.com users only
    // This allows Google Sheets functionality for internal admins
    if (isInternalDomain) {
      identityProvider.addScope('https://www.googleapis.com/auth/spreadsheets')
      identityProvider.addScope('https://www.googleapis.com/auth/drive.file')
    }

    identityProvider.setCustomParameters({ prompt: 'select_account' })

    return identityProvider
  },
  link() {
    return new EmailAuthProvider()
  },
  password() {
    return new EmailAuthProvider()
  },
  saml({ providerId }) {
    return new SAMLAuthProvider(providerId)
  },
}

const fireauth = {
  getCurrentUser() {
    return Fireauth.currentUser
  },

  onAuthStateChanged(fnSuccess, fnError) {
    return onAuthStateChanged(Fireauth, fnSuccess, fnError)
  },

  /**
   * Listen for ID token changes (including automatic refreshes).
   * Fires when the token is updated, refreshed, or user signs out.
   * @param {Function} callback - Called with user when token changes
   * @returns {Function} Unsubscribe function
   */
  onIdTokenChanged(callback) {
    return onIdTokenChanged(Fireauth, callback)
  },

  waitForFirebaseReady(options) {
    return waitForFirebaseReady(options)
  },

  /**
   * Force refresh the ID token.
   * @returns {Promise<string>} The refreshed token
   */
  async forceRefreshToken() {
    const user = Fireauth.currentUser
    if (!user) return null
    return user.getIdToken(true)
  },

  sendEmailVerification(url) {
    return sendEmailVerification(Fireauth.currentUser, {
      url,
      handleCodeInApp: true,
    })
  },

  sendSignInLinkToEmail(email, url) {
    return sendSignInLinkToEmail(Fireauth, email, {
      url,
      handleCodeInApp: true,
    })
  },

  isSignInWithEmailLink(email) {
    return isSignInWithEmailLink(Fireauth, email)
  },

  signInWithEmailLink(email, url) {
    return signInWithEmailLink(Fireauth, email, url)
  },

  signInWithCredential(credential) {
    return signInWithCredential(Fireauth, credential)
  },

  linkWithCredential(credential) {
    return linkWithCredential(Fireauth.currentUser, credential)
  },

  reauthenticateWithCredential(credential) {
    return reauthenticateWithCredential(Fireauth.currentUser, credential)
  },

  createUserWithEmailAndPassword(email, password) {
    return createUserWithEmailAndPassword(Fireauth, email, password)
  },

  updatePassword(password) {
    return updatePassword(Fireauth.currentUser, password)
  },

  signInWithPassword(email, password) {
    return signInWithEmailAndPassword(Fireauth, email, password)
  },

  /**
   * @function signInWithProvider
   * @desc Signs a user in with the given provider.
   * @param {string} providerDocument The provider's Firestore document.
   * @param {string} providerDocument.providerId The provider ID to use for sign-in.
   * @param {string} providerDocument.providerType The type of provider to use for sign-in.
   * @param {boolean} isInternalDomain Whether the sign-in is for an internal domain user.
   * @return {Promise<UserCredential>}
   */
  signInWithProvider({ providerId, providerType }, isInternalDomain = false) {
    const foundProvider = providerMap[providerType] !== undefined

    // Validate provider type.
    if (!foundProvider) {
      throw new Error(`Unsupported provider type: ${providerType}`)
    }

    // Initialize provider.
    const provider = providerMap[providerType]({ isInternalDomain, providerId })

    // Validate provider initialization.
    if (!provider) {
      throw new Error('Provider failed initialization')
    }

    // Handle password provider separately as it requires a different
    // sign-in method.
    if (providerType === 'password') {
      logger.log('Signing in with email and password using Password provider.', provider)

      const { email, password } = providerId

      // Sign in with email and password.
      return signInWithEmailAndPassword(Fireauth, email, password)
    }

    logger.log(`Signing in with ${providerType} provider using Popup method.`)

    // Prefer popup flow, but gracefully fall back to redirect in
    // environments that block popups (for example, integrated browsers).
    return signInWithPopup(Fireauth, provider).catch((error) => {
      // Check if the popup was blocked or closed before auth completed
      if (error.code === 'auth/popup-blocked'
        || error.code === 'auth/popup-closed-by-user'
        || error.code === 'auth/cancelled-popup-request'
        || error.message?.includes('popup')
        || error.message?.includes('blocked')
        || error.message?.includes('closed')) {
        logger.warn('[Auth] Popup sign-in unavailable, falling back to redirect sign-in.', {
          code: error.code,
          message: error.message,
          providerType,
        })

        return signInWithRedirect(Fireauth, provider)
      }
      // Re-throw other errors
      throw error
    })
  },

  signOut() {
    return signOut(Fireauth)
  },

  // Redirect flow is used as a fallback when popup auth is blocked.

  getIdToken() {
    return Fireauth.currentUser.getIdToken()
  },

  async getFirebaseAuthUser(email) {
    return Fireauth.getUserByEmail(email)
  },
}

export {
  fireauth,
  fireanalytics,
  firecollection,
  GoogleAuthProvider,
  Timestamp,
}
